import { Fragment } from 'react';
import LandingFooter from './LandingFooter.tsx';
import LandingNav from './LandingNav.tsx';
import HowItWorksStep from './HowItWorksStep.tsx';
import DealCard from './DealCard.tsx';
import FaqSection from './FaqSection.tsx';
import "./style.css";
import { HEADER_BACKGROUND_IMAGE } from './constants.ts';
import { HOW_IT_WORKS_STEPS, RECENTLY_CLOSED_DEALS, STEP_SEPARATOR_IMAGE } from './content.ts';

const StepSeparator = ({ flipped }: { flipped: boolean }): JSX.Element => (
    <div className="w-full flex justify-center">
        <img
            className="hidden xl:flex"
            style={flipped ? { transform: 'scaleY(-1)' } : undefined}
            src={STEP_SEPARATOR_IMAGE.src}
            alt={STEP_SEPARATOR_IMAGE.alt}
            width={STEP_SEPARATOR_IMAGE.width}
            height={STEP_SEPARATOR_IMAGE.height}
        />
    </div>
);

const Landing = (): JSX.Element => (
    <main id="landing" className="text-gray-800">
        <LandingNav className="absolute" showContactLink />

        <div
            id="hero"
            className="flex flex-col items-center justify-between px-6 pt-20 pb-20 md:px-24 md:pt-40 md:pb-40 bg-repeat-x"
            style={{ backgroundImage: `url(${HEADER_BACKGROUND_IMAGE})` }}
        >
            <div
                aria-hidden="true"
                className="absolute inset-0 bg-blue-900 opacity-70 bg-gradient-to-b from-blue-900/70 to-blue-900/60"
            ></div>

            <div className="container z-20">
                <h1 className="text-white mt-10 md:mt-0 text-4xl md:text-7xl font-bold">
                    Get The Hottest Leads<br/>
                    In Your Inbox, First
                </h1>
                <p className="hidden md:block mt-10 md:mt-20 text-white text-xl md:text-3xl md:max-w-2xl">
                    Connect instantly with property owners looking to sell fast. You set your bid per region,
                    and only pay for good leads!
                </p>
                <a href="/register">
                    <button
                        id="checkAvailabilityButton"
                        className="w-full md:w-auto text-center mt-20 px-8 md:px-20 py-6 text-xl md:text-3xl font-bold bg-orange-600 text-white"
                    >
                        Get Started Now!
                    </button>
                </a>
            </div>
        </div>

        <div
            id="how-it-work"
            className="relative md:flex flex-col items-center justify-center bg-white px-0 md:px-8 xl:px-0 pt-20 text-gray-800"
        >
            <div className="container">
                <h2 className="text-center mt-10 md:mt-0 mb-5 xl:mb-20 text-2xl md:text-5xl font-bold">
                    How does it work?
                </h2>
                {HOW_IT_WORKS_STEPS.map((step, index) => (
                    <Fragment key={step.title}>
                        <HowItWorksStep step={step} imageFirst={index % 2 === 1} />
                        {index < HOW_IT_WORKS_STEPS.length - 1 && (
                            <StepSeparator flipped={index % 2 === 1} />
                        )}
                    </Fragment>
                ))}
            </div>
        </div>

        <section className="dark:bg-[#0b1727] text-white z-[1] relative w-full bg-black">
            <div className="grid grid-cols-12 justify-center items-center bg-black">
                <div className="col-span-12 md:col-span-10 md:col-start-2 bg-black min-h-[80vh] flex flex-col justify-center items-center px-4 md:px-20 w-full text-center">
                    <h2 className="text-6xl md:text-[52px] lg:text-[65px] font-bold leading-tight mb-6">
                        Start making money with <span className="text-blue-600">the most innovative</span> real
                        estate lead gen platform.
                    </h2>
                    <p className="text-lg leading-6 text-left opacity-80 px-0 md:px-12">
                        Get leads tailored to your budget, and your investment regions. No risk - all leads are
                        disputable. Invest with a budget, or subscribe to receive free credits in your account
                        every month.
                    </p>
                    <div className="text-center mt-12">
                        <a href="/register">
                            <button className="bg-blue-600 border border-blue-600 text-white hover:bg-opacity-90 px-8 py-3 rounded transition m-2">
                                Let's Get Started!
                            </button>
                        </a>
                        <a href="/contact">
                            <button className="bg-transparent text-blue-600 border border-blue-600 hover:text-white hover:bg-blue-600 px-8 py-3 rounded transition m-2">
                                Contact Us
                            </button>
                        </a>
                    </div>
                </div>
            </div>
        </section>

        <div className="flex flex-col items-center justify-center bg-white my-10 xl:pt-36 xl:pb-0">
            <div className="container">
                <h2 className="text-2xl md:text-5xl font-bold text-center px-6 md:px-0">Recently Closed Deals</h2>
                <p className="text-lg md:text-xl mt-5 text-center px-6 md:px-0">Example leads from the past month</p>
                <div className="xl:grid xl:grid-cols-3 md:gap-16 mt-0 md:mt-20 px-6 md:px-10">
                    {RECENTLY_CLOSED_DEALS.map((deal) => (
                        <DealCard key={deal.image.src} deal={deal} />
                    ))}
                </div>
                <a href="/register">
                    <p className="text-center font-bold text-xl md:text-3xl text-blue-900 mt-8 md:mt-16">
                        View last month's deals&gt;
                    </p>
                </a>
            </div>
        </div>

        <FaqSection/>

        <LandingFooter/>
    </main>
);

export default Landing;
